import React, { memo } from 'react';
import { theme } from '../../theme';

interface PageTitleProps {
  /** Texto del título */
  children: React.ReactNode;
  /** Clase CSS adicional */
  className?: string;
  /** Estilos personalizados */
  style?: React.CSSProperties;
  /** Nivel de título (h1, h2, h3) */
  level?: 1 | 2 | 3;
}

/**
 * Componente reutilizable para títulos de página
 * Mantiene consistencia en tipografía y colores
 * 
 * Optimizado con React.memo para evitar re-renders innecesarios
 * 
 * @example
 * <PageTitle>Mi Título</PageTitle>
 * <PageTitle level={2}>Subtítulo</PageTitle>
 */
export const PageTitle = memo<PageTitleProps>(({ 
  children, 
  className = '',
  style = {},
  level = 1,
}) => {
  const fontSizeMap = {
    1: '28px',
    2: '24px',
    3: '20px',
  };

  const marginBottomMap = {
    1: '24px',
    2: '20px',
    3: '16px',
  };

  const Tag = `h${level}` as keyof JSX.IntrinsicElements;

  return (
    <Tag
      className={className}
      style={{ 
        color: theme.colors.primaryDark, 
        fontSize: fontSizeMap[level], 
        fontWeight: 600, 
        marginBottom: marginBottomMap[level],
        marginTop: 0,
        ...style
      }}
    >
      {children}
    </Tag>
  );
});

PageTitle.displayName = 'PageTitle';

